import { useCallback, useState } from 'react';

import type { User } from '../../../../services';
import type { EntitlementRow } from '../IncludedEntitlements';

interface UseEntitlementDeletionParams {
  selectedRowData: EntitlementRow | User | null;
  setSelectedRowData: React.Dispatch<
    React.SetStateAction<EntitlementRow | User | null>
  >;
  handleCloseSidePanel: () => void;
  setDeletedEntGroups: React.Dispatch<React.SetStateAction<Set<string>>>;
}

/**
 * Custom hook to manage entitlement group deletion functionality
 * Handles modal state, confirmation logic, and cleanup of related state
 */
// eslint-disable-next-line import/prefer-default-export
export const useEntitlementDeletion = ({
  selectedRowData,
  setSelectedRowData,
  handleCloseSidePanel,
  setDeletedEntGroups,
}: UseEntitlementDeletionParams) => {
  // Modal and deletion state
  const [entGroupBeingDeleted, setEntGroupBeingDeleted] =
    useState<EntitlementRow | null>(null);
  const [isDeleteEntGroupModalOpen, setIsDeleteEntGroupModalOpen] =
    useState(false);

  const handleDeleteEntRowClick = useCallback((entitlement: EntitlementRow) => {
    setEntGroupBeingDeleted(entitlement);
    setIsDeleteEntGroupModalOpen(true);
  }, []);

  // Handler for when user confirms deletion in modal
  const handleConfirmEntRowDelete = useCallback((): void => {
    if (!entGroupBeingDeleted) {
      return;
    }

    // 1. No need to handle expanded state for individual entitlement rows

    // 2. Handle side panel cleanup - close if showing the deleted entitlement
    if (
      selectedRowData &&
      'evKey' in selectedRowData &&
      selectedRowData.evKey === entGroupBeingDeleted.evKey
    ) {
      setSelectedRowData(null);
      handleCloseSidePanel();
    }

    // 3. Track deletion (source of truth) - this will trigger UI updates via activeEntitlementRows
    setDeletedEntGroups(
      (prev) => new Set([...prev, entGroupBeingDeleted.evKey.toString()])
    );

    // 4. Clean up modal state
    setIsDeleteEntGroupModalOpen(false);
    setEntGroupBeingDeleted(null);
  }, [
    entGroupBeingDeleted,
    selectedRowData,
    setSelectedRowData,
    handleCloseSidePanel,
    setDeletedEntGroups,
  ]);

  // Handler for when user cancels deletion
  const handleCancelDelete = useCallback(() => {
    setIsDeleteEntGroupModalOpen(false);
    setEntGroupBeingDeleted(null);
  }, []);

  return {
    entGroupBeingDeleted,
    isDeleteEntGroupModalOpen,
    handleDeleteEntRowClick,
    handleConfirmEntRowDelete,
    handleCancelDelete,
  };
};
